<div class="blogs-news-page">
	<!-- Hero Section -->
	<header class="hero">
		<div class="hero-content">
			<h1 class="hero-title">
				<i class="pi pi-rss"></i>
				{{ title }}
			</h1>
			<p class="hero-subtitle">Stay updated with the latest blogs, news, and announcements from our team.</p>
		</div>
	</header>

	<ng-container *ngIf="blogPostsView$ | async as vm">
		<!-- Toolbar: search + label filter -->
		<section class="toolbar" *ngIf="!vm.loading">
			<div class="toolbar-search-row">
				<div class="search-wrapper">
					<i class="pi pi-search search-icon"></i>
					<input type="text" class="search-input" placeholder="Search posts..." [ngModel]="searchQuery()" (ngModelChange)="searchQuery.set($event)" />
				</div>
				<button *ngIf="hasActiveFilters()" type="button" class="clear-all-filters-btn" (click)="clearFilters()" aria-label="Clear all filters">
					<i class="pi pi-filter-slash"></i>
					<span>Clear all filters</span>
				</button>
			</div>

			<div class="date-time-filters">
				<div class="dt-field">
					<label class="dt-label" for="filter-date-from">
						<i class="pi pi-calendar-plus"></i>
						From
					</label>
					<input
						id="filter-date-from"
						type="datetime-local"
						class="dt-input"
						[class.dt-input-invalid]="isDateRangeInvalid()"
						[attr.aria-invalid]="isDateRangeInvalid() ? 'true' : null"
						[ngModel]="filterDateFrom()"
						(ngModelChange)="filterDateFrom.set($event)"
					/>
				</div>
				<div class="dt-field">
					<label class="dt-label" for="filter-date-to">
						<i class="pi pi-calendar-minus"></i>
						To
					</label>
					<input
						id="filter-date-to"
						type="datetime-local"
						class="dt-input"
						[class.dt-input-invalid]="isDateRangeInvalid()"
						[attr.aria-invalid]="isDateRangeInvalid() ? 'true' : null"
						[ngModel]="filterDateTo()"
						(ngModelChange)="filterDateTo.set($event)"
					/>
				</div>
				<div class="dt-clear-wrap" *ngIf="hasDateFilters()">
					<button type="button" class="dt-clear-btn" (click)="clearDateFilters()" aria-label="Clear date and time filters">
						<i class="pi pi-times"></i>
						<span>Clear date/time</span>
					</button>
				</div>
			</div>
			<p class="dt-range-error" *ngIf="isDateRangeInvalid()" role="alert">
				<i class="pi pi-exclamation-circle" aria-hidden="true"></i>
				<span>“To” must be the same time or later than “From”. Adjust the range or clear the date fields.</span>
			</p>

			<div class="publication-filters" *ngIf="canEdit$ | async">
				<span class="publication-filters-label">
					<i class="pi pi-filter"></i>
					Publication
				</span>
				<div class="publication-filters-chips">
					<button
						type="button"
						class="label-chip pub-chip-published"
						[class.active]="isPublicationFilterActive('published')"
						pTooltip="Published posts — click again to clear"
						(click)="togglePublicationFilter('published')"
					>
						<i class="pi pi-check-circle"></i>
						Published
					</button>
					<button
						type="button"
						class="label-chip pub-chip-draft"
						[class.active]="isPublicationFilterActive('draft')"
						pTooltip="Drafts — click again to clear"
						(click)="togglePublicationFilter('draft')"
					>
						<i class="pi pi-file-edit"></i>
						Drafts
					</button>
					<button
						type="button"
						class="label-chip pub-chip-archive"
						[class.active]="isPublicationFilterActive('archived')"
						pTooltip="Archived — click again to clear"
						(click)="togglePublicationFilter('archived')"
					>
						<i class="pi pi-box"></i>
						Archive
					</button>
					<button
						type="button"
						class="label-chip pub-chip-untagged"
						[class.active]="isPublicationFilterActive('untagged')"
						pTooltip="Posts with no labels — click again to clear"
						(click)="togglePublicationFilter('untagged')"
					>
						<i class="pi pi-tag"></i>
						Untagged
					</button>
				</div>
			</div>

			<div class="label-filters" *ngIf="extractAllLabels(postsForLabelOptions(vm.posts)).length > 0">
				<button *ngFor="let label of extractAllLabels(postsForLabelOptions(vm.posts))" class="label-chip" [class.active]="isLabelActive(label)" (click)="toggleLabel(label)">
					{{ label }}
				</button>
			</div>
		</section>

		<!-- Skeleton while fetching posts -->
		<section class="posts-section posts-skeleton-section" *ngIf="vm.loading" aria-busy="true" [attr.aria-label]="'Loading blog posts'">
			<div class="posts-masonry">
				<article *ngFor="let _ of skeletonPlaceholders" class="post-card post-card-skeleton">
					<div class="skeleton-block skeleton-label"></div>
					<div class="skeleton-block skeleton-title"></div>
					<div class="skeleton-block skeleton-title narrow"></div>
					<div class="skeleton-row skeleton-meta">
						<div class="skeleton-block skeleton-pill"></div>
						<div class="skeleton-block skeleton-pill"></div>
					</div>
					<div class="skeleton-block skeleton-line"></div>
					<div class="skeleton-block skeleton-line"></div>
					<div class="skeleton-block skeleton-line medium"></div>
					<div class="skeleton-footer">
						<div class="skeleton-block skeleton-link"></div>
					</div>
				</article>
			</div>
		</section>

		<!-- Error Display -->
		<p-messages *ngIf="error()" severity="error" [value]="[{ severity: 'error', summary: 'Error', detail: error() }]" class="error-banner"></p-messages>

		<!-- Loading -->
		<div *ngIf="isLoading()" class="loading-overlay">
			<p-progressSpinner strokeWidth="3" animationDuration="0.8s"></p-progressSpinner>
		</div>

		<!-- Blog Posts -->
		<section class="posts-section" *ngIf="!vm.loading">
			<div class="posts-masonry" *ngIf="filterPosts(vm.posts) as filteredPosts">
				<article
					*ngFor="let post of filteredPosts | slice: 0 : displayLimit(); let i = index"
					class="post-card"
					[class.not-accepted]="post.accepted === false"
					[class.archived]="post.archived === true"
					role="button"
					tabindex="0"
					(click)="onPostCardClick($event, post)"
					(keydown.enter)="openReadMoreDialog(post)"
					(keydown.space)="$event.preventDefault(); openReadMoreDialog(post)"
				>
					<!-- Status ribbon for editors -->
					<div class="card-status" *ngIf="canEdit$ | async">
						<span class="status-dot draft" *ngIf="post.accepted === false" pTooltip="Draft — not yet published"></span>
						<span class="status-dot archived-dot" *ngIf="post.archived" pTooltip="Archived"></span>
					</div>

					<!-- Labels (same text as issueKey is omitted — shown only as Jira link in meta when valid) -->
					<div class="card-labels" *ngIf="labelsForDisplay(post).length">
						<span class="card-label" *ngFor="let label of labelsForDisplay(post)">{{ label }}</span>
					</div>

					<!-- Title -->
					<h2 class="card-title">{{ extractTitle(post.post) }}</h2>

					<!-- Meta row -->
					<div class="card-meta">
						<span class="meta-date" *ngIf="post.createdAt">
							<i class="pi pi-calendar"></i>
							{{ formatTimestamp(post.createdAt) }}
						</span>
						<span class="meta-read-time">
							<i class="pi pi-clock"></i>
							{{ estimateReadTime(post.post) }} min read
						</span>
						<a
							*ngIf="hasJiraIssueKey(post.issueKey)"
							class="jira-issue-link"
							[href]="jiraIssueBrowseUrl(post.issueKey)"
							target="_blank"
							rel="noopener noreferrer"
							(click)="$event.stopPropagation()"
							[attr.title]="'Open ' + post.issueKey + ' in Jira'"
						>
							<i class="pi pi-tag"></i>
							{{ post.issueKey }}
						</a>
					</div>

					<!-- Preview -->
					<div class="card-excerpt" [innerHTML]="markdownBodyHtml(truncateText(extractContent(post.post), 180))"></div>

					<!-- Footer -->
					<div class="card-footer">
						<span class="read-more-link">
							Read more
							<i class="pi pi-arrow-right"></i>
						</span>
						<!-- Editor actions (stop propagation so card click doesn't fire) -->
						<div class="editor-actions" *ngIf="canEdit$ | async">
							<button type="button" class="action-btn" [pTooltip]="post.archived ? 'Unarchive' : 'Archive'" (click)="$event.stopPropagation(); toggleArchive(post)">
								<i [class]="post.archived ? 'pi pi-replay' : 'pi pi-box'"></i>
							</button>
							<button type="button" class="action-btn" pTooltip="Edit" (click)="$event.stopPropagation(); startEdit(i, post)">
								<i class="pi pi-pencil"></i>
							</button>
						</div>
					</div>
				</article>
			</div>

			<!-- Show More -->
			<div class="show-more" *ngIf="filterPosts(vm.posts).length > displayLimit()">
				<button class="show-more-btn" (click)="increaseLimit()">
					<span>Load more posts</span>
					<i class="pi pi-chevron-down"></i>
				</button>
			</div>

			<!-- Active filters count -->
			<div class="results-info" *ngIf="hasActiveFilters()">
				<span>{{ filterPosts(vm.posts).length }} results</span>
				<button class="clear-link" (click)="clearFilters()">Clear filters</button>
			</div>

			<!-- Empty State -->
			<div class="empty-state" *ngIf="filterPosts(vm.posts).length === 0">
				<div class="empty-icon">
					<i class="pi pi-inbox"></i>
				</div>
				<h3>{{ hasActiveFilters() ? 'No posts match your filters' : 'No blog posts yet' }}</h3>
				<p>{{ hasActiveFilters() ? 'Try adjusting your search, dates, publication filter, or clearing filters.' : 'Check back later for new content!' }}</p>
				<button *ngIf="hasActiveFilters()" class="show-more-btn" (click)="clearFilters()">Clear filters</button>
			</div>
		</section>
	</ng-container>

	<!-- Read More Dialog -->
	<p-dialog
		[(visible)]="isReadMoreDialogVisible"
		[modal]="true"
		[dismissableMask]="true"
		[style]="{ width: '72vw', maxWidth: '860px' }"
		[breakpoints]="{ '960px': '95vw' }"
		[showHeader]="false"
		(onHide)="closeReadMoreDialog()"
		styleClass="read-more-dialog"
	>
		<div class="article-view" *ngIf="readMorePost">
			<div class="article-top-actions">
				<button *ngIf="canEdit$ | async" type="button" class="article-edit" pTooltip="Edit post" (click)="editPostFromReadMore()">
					<i class="pi pi-pencil"></i>
				</button>
				<button type="button" class="article-close" (click)="closeReadMoreDialog()" aria-label="Close">
					<i class="pi pi-times"></i>
				</button>
			</div>

			<!-- Labels (same text as issueKey omitted — shown as Jira link in meta when valid) -->
			<div class="article-labels" *ngIf="labelsForDisplay(readMorePost).length">
				<span class="article-label" *ngFor="let label of labelsForDisplay(readMorePost)">{{ label }}</span>
			</div>

			<!-- Title -->
			<h1 class="article-title">{{ extractTitle(readMorePost.post) }}</h1>

			<!-- Meta -->
			<div class="article-meta">
				<span *ngIf="readMorePost.createdAt">
					<i class="pi pi-calendar"></i>
					{{ formatTimestamp(readMorePost.createdAt) }}
				</span>
				<span>
					<i class="pi pi-clock"></i>
					{{ estimateReadTime(readMorePost.post) }} min read
				</span>
				<a
					*ngIf="hasJiraIssueKey(readMorePost.issueKey)"
					class="jira-issue-link"
					[href]="jiraIssueBrowseUrl(readMorePost.issueKey)"
					target="_blank"
					rel="noopener noreferrer"
					[attr.title]="'Open ' + readMorePost.issueKey + ' in Jira'"
				>
					<i class="pi pi-tag"></i>
					{{ readMorePost.issueKey }}
				</a>
			</div>

			<!-- Content -->
			<div class="article-body" [innerHTML]="markdownBodyHtml(extractContent(readMorePost.post))"></div>
		</div>
	</p-dialog>

	<!-- Modal Editor -->
	<p-dialog
		[(visible)]="isEditorDialogVisible"
		[modal]="true"
		[style]="{ width: '70vw', maxWidth: '960px' }"
		[breakpoints]="{ '960px': '95vw' }"
		header="Edit Post"
		(onHide)="closeEditorDialog()"
		styleClass="editor-dialog"
	>
		<div class="editor-form">
			<label class="editor-label" for="editor-title-input">Title</label>
			<input id="editor-title-input" pInputText type="text" [(ngModel)]="editedTitle" placeholder="Post title" class="editor-title-input" />

			<label class="editor-label" for="editor-content-input">
				Content
				<span class="hint">(Markdown supported)</span>
			</label>
			<textarea
				id="editor-content-input"
				pInputTextarea
				[(ngModel)]="editedTextValue"
				[autoResize]="true"
				rows="14"
				class="editor-content-input"
				placeholder="Write your post content..."
			></textarea>

			<div class="editor-options">
				<div class="option-group">
					<span class="editor-label" id="editor-published-label">Published</span>
					<p-selectButton [options]="acceptedOptions" [(ngModel)]="editedAccepted" optionLabel="label" optionValue="value" [ariaLabelledBy]="'editor-published-label'"></p-selectButton>
				</div>
				<div class="option-group">
					<span class="editor-label" id="editor-archived-label">Archived</span>
					<p-selectButton [options]="archivedOptions" [(ngModel)]="editedArchived" optionLabel="label" optionValue="value" [ariaLabelledBy]="'editor-archived-label'"></p-selectButton>
				</div>
				<div class="option-group full-width">
					<label class="editor-label" for="editor-labels-input">
						Labels
						<span class="hint">(comma separated)</span>
					</label>
					<input id="editor-labels-input" pInputText type="text" [(ngModel)]="editedIssueLabels" placeholder="label1, label2" class="editor-labels-input" />
				</div>
				<div class="option-group full-width" *ngIf="canEdit$ | async">
					<label class="editor-label" for="editor-issue-key-input">
						Jira issue
						<span class="hint">(e.g. PROJ-123; leave empty if none)</span>
					</label>
					<input id="editor-issue-key-input" pInputText type="text" [(ngModel)]="editedIssueKey" placeholder="PROJ-123" class="editor-labels-input" autocomplete="off" spellcheck="false" />
				</div>
			</div>

			<div class="editor-actions-bar">
				<button pButton type="button" label="Save Changes" icon="pi pi-check" aria-label="Save Changes" class="p-button-sm" (click)="dialogContextPost && saveEdit(dialogContextPost)"></button>
				<button pButton type="button" label="Discard" icon="pi pi-times" aria-label="Discard" class="p-button-sm p-button-outlined p-button-secondary" (click)="closeEditorDialog()"></button>
			</div>
		</div>
	</p-dialog>
</div>
